import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { getToken, getCurrentUser } from "@/lib/api";

/**
 * Dark-themed shell — spec's Sidebar (collapsible, mobile-aware) on the
 * left, page content on the right. We compose Sidebar directly here to
 * keep the auth guard + sign-out callback in the same component.
 */
export function AdminShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const token = getToken();
  const user = getCurrentUser<{ email: string; isAdmin: boolean }>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useLocation().pathname;

  useEffect(() => {
    if (!token) navigate("/login", { replace: true });
    else if (user && !user.isAdmin) navigate("/login", { replace: true });
  }, [token, user, navigate]);

  if (!token) return null;

  // Login page renders without the shell.
  if (pathname === "/login") return <>{children}</>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col md:flex-row w-full overflow-x-hidden">
      <Sidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={() => {
          if (typeof window !== "undefined" && window.innerWidth < 768) {
            setMobileOpen(!mobileOpen);
          } else {
            setIsCollapsed(!isCollapsed);
          }
        }}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      <main className="flex-1 min-w-0 flex flex-col min-h-[calc(100vh-64px)] md:min-h-screen">
        <div className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={pathname}
              initial={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 0.98, filter: "blur(4px)", transition: { duration: 0.2 } }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="w-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
